import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ErrorPage from "pages/ErrorPage/ErrorPage";

import facilityPageSelector from "store/selectors/FacilityPage";

import { Page } from "components/core/Page";
import Container from "components/core/Page/Container";
import { BannerWithIcon } from "components/core/BannerWithIcon";
import { ScrollToTop } from "components/core/ScrollToTop";
import { Clock, Exclamation } from "components/core/Icons";
import { FacilityPageHeader } from "components/facility/FacilityPageHeader";
import { Button } from "components/core/Button";
import UnitsList from "components/facility/UnitsList/UnitsList";
import FacilityUnitMicroData from "components/facility/FacilityUnitMicroData";
import { Schema } from "@sparefoot/react-commons";

import {
	DescriptionSection,
	LocationSection,
	HoursSection,
	WhySection,
	ReviewsSection,
	NearbyFacilitiesSection,
	SearchSection,
	FeaturesAndAmenitiesSection,
	ImagesSection,
	NearbyFacilitiesUnits
} from "components/facility/sections";

import "./FacilityPage.scss";
import { TrackingClient } from "@sparefoot/tracking-client";
import usePageViewTracking from "hooks/tracking/usePageViewTracking";
import { PAGE_TYPES } from "@sparefoot/tracking-events";

function FacilityPage() {
	// useParams must be called unconditionally — Rules of Hooks.
	// RR v5 used :facilityId([0-9]+) in the path regex to reject non-numeric slugs before
	// reaching this component. RR v6 removed inline regex support so the route now matches
	// any slug — we derive the id here and guard after all hooks have run.
	const { facilitySlug } = useParams();
	const slugFacilityId = facilitySlug?.slice(
		facilitySlug.lastIndexOf("-") + 1
	);
	const isInvalidSlug =
		facilitySlug !== undefined &&
		(!slugFacilityId || !/^\d+$/.test(slugFacilityId));

	const {
		meta,
		facilityId,
		headerProps,
		imagesProps,
		descriptionProps,
		locationModuleProps,
		hoursProps,
		feesProps,
		reviewsProps,
		nearbyFacilitiesProps,
		featuresAndAmenitiesProps,
		facilityUnits,
		nearbyFacilitiesUnits,
		closures,
		nearbyFacilitiesUrl,
		hasUnits,
		location,
		promotions
	} = useSelector(facilityPageSelector);
	const baseUrl =
		useSelector((state) => state?.app?.baseUrl) ||
		"https://www.storage.com";

	usePageViewTracking(PAGE_TYPES.FACILITY);

	useEffect(() => {
		TrackingClient.trackFacilityViewed({
			facility_id: facilityId,
			facility_name: headerProps.name,
			facility_promo: promotions,
			facility_rank: "" // need to check where to fetch rank
		});
	}, []);

	if (isInvalidSlug) return <ErrorPage statusCode={404} />;

	const mapSchema = {
		"@context": "https://schema.org",
		"@type": "Map",
		name: "Facility Map",
		url: `https://www.google.com/maps?q=${location?.latitude},${location?.longitude}`
	};

	return (
		<Page
			className="facility-page"
			pageType={PAGE_TYPES.FACILITY}
			title={meta.title}
			description={meta.description}
			noIndex={meta.noIndex}
			noFollow={meta.noFollow}
			canonical={meta.canonical}
			socialImageUrl={imagesProps?.images[0]}
		>
			{/* Scroll To Top */}
			{hasUnits && <ScrollToTop />}
			{/* No Units Banner */}
			{!hasUnits && (
				<BannerWithIcon
					icon={<Exclamation />}
					text="This facility has no units available for Storage.com online booking."
					textSize="large"
					textWeight="bold"
					theme="outlined-orange-bold"
					fixed
				>
					<Button
						href={nearbyFacilitiesUrl}
						theme="primary"
					>
						See Nearby Facilities
					</Button>
				</BannerWithIcon>
			)}
			{/* Header */}
			<FacilityPageHeader {...headerProps} />
			{/* Page Content */}
			<Container className="page-sections">
				{/* Main Content Top */}
				<section>
					{/* No Units Banner */}
					{!hasUnits && (
						<BannerWithIcon
							icon={<Exclamation />}
							text="This facility has no units available for Storage.com online booking."
							theme="outlined-orange"
						/>
					)}

					{/* Image Carousel */}
					<ImagesSection {...imagesProps} />

					{/* Call Volume Banner */}
					<BannerWithIcon
						icon={<Clock />}
						theme="background-orange"
						text="We are experiencing higher than normal call volume. RESERVE FOR FREE ONLINE for faster service!"
					/>

					{/* Nearby Facility Units */}
					{!hasUnits && (
						<NearbyFacilitiesUnits units={nearbyFacilitiesUnits} />
					)}

					{/* Storage and Vehicle Unit Lists */}
					{hasUnits && (
						<div className="units-lists">
							{facilityUnits.storageUnits && (
								<UnitsList
									title="Storage Units"
									units={facilityUnits.storageUnits}
									closures={closures}
									facilityId={meta.id}
									nearbyFacilitiesUrl={nearbyFacilitiesUrl}
									cityUrl={headerProps.cityUrl}
									facilityName={headerProps.name}
									location={location}
									{...feesProps}
								/>
							)}
							{facilityUnits.vehicleUnits && (
								<UnitsList
									title="Vehicle Storage"
									units={facilityUnits.vehicleUnits}
									closures={closures}
									facilityId={meta.id}
									nearbyFacilitiesUrl={nearbyFacilitiesUrl}
									cityUrl={headerProps.cityUrl}
									facilityName={headerProps.name}
									location={location}
								/>
							)}
						</div>
					)}
				</section>

				{/* Sidebar */}
				<aside>
					<FacilityUnitMicroData
						storageUnits={facilityUnits?.storageUnits || []}
						vehicleUnits={facilityUnits?.vehicleUnits || []}
						facilityName={headerProps.name}
						facilityUrl={headerProps.url}
						facilityImageUrl={imagesProps?.images[0]}
						facilityId={facilityId}
						baseUrl={baseUrl}
					/>
					<Schema schema={mapSchema} />
					<LocationSection {...locationModuleProps} />
					<HoursSection {...hoursProps} />
					<DescriptionSection {...descriptionProps} />
					<FeaturesAndAmenitiesSection
						{...featuresAndAmenitiesProps}
					/>
					<SearchSection />
					<WhySection />
				</aside>

				{/* Main Content Bottom */}
				<section>
					<ReviewsSection {...reviewsProps} />
					<SearchSection />
					<NearbyFacilitiesSection {...nearbyFacilitiesProps} />
				</section>
			</Container>
		</Page>
	);
}

export default FacilityPage;
